import type { Normalizer } from '@change-corgi/fcm/normalizers';
import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';
import type { FcmMerger } from '../shared/types';

/**
 * Variants of the `homepage_hero_experiment` GrowthBook experiment, copied by hand: the configs
 * barrel is consumed as "every export is an `FcmConfig`", so the experiment can't import this list
 * from here. A variant missing from it fails the front-end typecheck, where `useHeroFeatureFlags`
 * indexes this record by the experiment's variation.
 */
const HERO_VARIANTS = ['control', 'issue_prompt', 'social_post', 'social_post_new'] as const;

type HeroVariant = (typeof HERO_VARIANTS)[number];

/** Flags available to every variant. Each is optional so a value only names the ones it decides. */
const HERO_FEATURE_FLAGS_SHAPE = {
	type: 'object',
	shape: {
		typingPlaceholderAnimation: { type: 'boolean', optional: true },
	},
} as const;

type HeroVariantFlags = Readonly<{ typingPlaceholderAnimation?: boolean }>;

type HeroFeatureFlags = Partial<Readonly<Record<HeroVariant, HeroVariantFlags>>>;

const normalizeShape = createShapeNormalizer({
	type: 'record',
	keys: HERO_VARIANTS,
	optionalValues: true,
	shape: HERO_FEATURE_FLAGS_SHAPE,
});

const HERO_FEATURE_FLAG_NAMES = Object.keys(HERO_FEATURE_FLAGS_SHAPE.shape);

/**
 * Rejects unrecognized variants and flag names on top of the shape check. The shape normalizer
 * silently drops keys it doesn't know, so a misspelled variant would otherwise be saved and then
 * never match a hero, and a misspelled flag would save as an empty object that reads as off.
 */
const normalizeHeroFeatureFlags: Normalizer<HeroFeatureFlags> = (value: unknown) => {
	const flags = normalizeShape(value);

	for (const [variant, variantValue] of Object.entries(value as Record<string, Record<string, unknown> | undefined>)) {
		if (!(HERO_VARIANTS as readonly string[]).includes(variant)) {
			throw new Error(`Unknown homepage hero variant "${variant}" - expected one of: ${HERO_VARIANTS.join(', ')}`);
		}

		// the shape normalizer has already rejected a variant value that isn't an object
		for (const flag of Object.keys(variantValue ?? {})) {
			if (!HERO_FEATURE_FLAG_NAMES.includes(flag)) {
				throw new Error(
					`Unknown homepage hero flag "${flag}" on variant "${variant}" - expected one of: ${HERO_FEATURE_FLAG_NAMES.join(', ')}`,
				);
			}
		}
	}

	return flags;
};

/**
 * Drops the flags a value leaves unset. The normalizer materializes every optional flag as an own
 * property, so an unset flag is an own `undefined` that a spread would write over a set one.
 */
function definedFlags(flags: HeroVariantFlags | undefined): HeroVariantFlags {
	const entries: ReadonlyArray<[string, boolean | undefined]> = Object.entries(flags ?? {});

	return Object.fromEntries(entries.filter(([, flag]) => flag !== undefined));
}

/**
 * Layers a country's value over the global one per variant, so a country entry only lists the flags
 * it changes. The default merge would replace the whole object.
 */
const mergeHeroFeatureFlags: FcmMerger<HeroFeatureFlags> = ({ globalValue, countryValue }) => {
	const variants = new Set<string>([...Object.keys(globalValue), ...Object.keys(countryValue)]);

	return Object.fromEntries(
		[...variants].map((variant) => [
			variant,
			{
				...definedFlags(globalValue[variant as HeroVariant]),
				...definedFlags(countryValue[variant as HeroVariant]),
			},
		]),
	);
};

export const FCM_HOMEPAGE_HERO_FEATURE_FLAGS = defineFcm('homepage_hero_feature_flags')
	.asJson(normalizeHeroFeatureFlags)
	.withDefault({})
	.withMerger(mergeHeroFeatureFlags)
	.toConfig({
		owner: 'squad-narwhal',
		scope: 'corgi-front-end',
		context: 'Homepage hero (homepage_hero_experiment variants)',
		description: `Per-variant feature toggles for the homepage hero, so a feature can be turned on for some hero variants and not others.

			Keyed by a variant of the homepage_hero_experiment (omitted variants default to false)
			• ${HERO_VARIANTS.join('\n • ')}

			Flags:
			• typingPlaceholderAnimation: types example petition topics over the hero's input, one at a time, instead of a static placeholder.

			Every flag defaults to off, so a variant only needs an entry to turn something on.`,
		mergeLogic:
			"A country's value is layered over the global one variant by variant, so a country entry only lists the flags it changes. Flags absent from both are off.",
		supportedGroups: { global: true, countries: true },
		template: {
			issue_prompt: { typingPlaceholderAnimation: true },
		},
	});
